import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import party from "party-js";
import { useAuth } from "../../../contexts/AuthContext";
import { APP_SHELL_LAYOUT } from "../../../constants/constants";
import {
  getClaimDisplayState,
  getClaimResultPresentation,
  subscribeRaffleTicketView,
} from "../../../services/claimsService";
import { followCampaignBusiness } from "../../../services/claimFollowService";
import { getClaimHeroImageAspectRatioCss } from "../../../utils/claimHeroImage";
import { getSuccessContainedDarkTextSx } from "./claimActionStyles";
import ClaimFixedAction from "./ClaimFixedAction";
import ClaimRedeemQrAction from "./ClaimRedeemQrAction";
import OfficialRulesDisclosure from "./OfficialRulesDisclosure";

function resolveBusinessName(business = {}) {
  return String(business?.companyName || business?.displayName || business?.name || "").trim();
}

function resolveToneColor(theme, tone) {
  if (tone === "success") return theme.palette.success.main;
  if (tone === "warning") return theme.palette.warning.main;
  if (tone === "info") return theme.palette.info.main;
  return theme.palette.text.secondary;
}

function ResultIcon({ icon, sx }) {
  if (icon === "check") return <CheckCircleOutlineIcon sx={sx} />;
  if (icon === "pending") return <HourglassEmptyOutlinedIcon sx={sx} />;
  return <RadioButtonUncheckedOutlinedIcon sx={sx} />;
}

export default function ClaimTicketPage({ campaignType = "raffle" }) {
  const { ticketToken } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [view, setView] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState("");
  const followedBusinessRef = React.useRef(new Set());
  const celebrationTargetRef = React.useRef(null);
  const celebratedTicketRef = React.useRef("");

  React.useEffect(() => {
    if (!currentUser) {
      navigate(`/signin?next=${encodeURIComponent(`${location.pathname}${location.search || ""}`)}`, {
        replace: true,
      });
      return;
    }

    let hasLoaded = false;
    setLoading(true);
    setErrorMessage("");

    return subscribeRaffleTicketView(ticketToken, {
      userUid: currentUser.uid,
      onNext: (nextView) => {
        hasLoaded = true;
        setView(nextView);
        setErrorMessage("");
        setLoading(false);
      },
      onError: (error) => {
        setErrorMessage(error?.message || "Unable to load this ticket.");
        if (!hasLoaded) setLoading(false);
      },
    });
  }, [currentUser, location.pathname, location.search, navigate, ticketToken]);

  React.useEffect(() => {
    const businessId = String(view?.raffle?.business?.id || view?.raffle?.businessId || "").trim();
    if (!currentUser?.uid || !businessId || followedBusinessRef.current.has(businessId)) return;
    followedBusinessRef.current.add(businessId);
    followCampaignBusiness(currentUser.uid, view?.raffle).catch((error) => {
      followedBusinessRef.current.delete(businessId);
      console.warn("Unable to follow ticket business:", error);
    });
  }, [currentUser?.uid, view?.raffle]);

  const type = view?.raffle?.campaignType || campaignType;
  const isCoupon = type === "coupon";
  const state = getClaimDisplayState({
    campaignType: type,
    ticketStatus: view?.ticket?.ticketStatus,
    redeemedAtMs: view?.ticket?.redeemedAtMs,
    claimEndAt: view?.raffle?.claimEndAt,
  });
  const result = getClaimResultPresentation({
    campaignType: type,
    ticketStatus: view?.ticket?.ticketStatus,
    redeemedAtMs: view?.ticket?.redeemedAtMs,
    claimEndAt: view?.raffle?.claimEndAt,
    prizeTitle: view?.ticket?.prizeSnapshot?.title,
  });
  const resultColor = resolveToneColor(theme, result.tone);
  const businessName = resolveBusinessName(view?.raffle?.business || {});
  const businessInitial = businessName ? businessName.charAt(0).toUpperCase() : "I";
  const heroImageUrl = String(view?.raffle?.heroImageUrl || "").trim();
  const heroImageAspectRatio = getClaimHeroImageAspectRatioCss(view?.raffle?.heroImage);
  const redeemUrl = view?.ticket?.redeemUrl || "";
  const prizeTitle = String(view?.ticket?.prizeSnapshot?.title || "").trim();
  const prizeDescription = String(view?.ticket?.prizeSnapshot?.description || "").trim();
  const governingTermsText = isCoupon
    ? String(view?.raffle?.couponTerms?.text || "").trim()
    : String(view?.raffle?.officialRules?.text || "").trim();
  const governingTermsTitle = isCoupon ? "Coupon Terms" : "Official Rules";
  const governingTermsHelperText = isCoupon ? "View coupon terms" : "View raffle fine print";
  const showQr = state.isUsable && Boolean(String(redeemUrl || "").trim());
  const redemptionMessage = view?.message || result.body || "Keep this pass for your records.";
  const fixedActionLabel = showQr ? result.actionLabel || "Scan to redeem" : result.actionLabel;
  const claimCelebration = location?.state?.claimCelebration || null;
  const hasCouponClaimCelebration =
    isCoupon &&
    claimCelebration?.campaignType === "coupon" &&
    claimCelebration?.ticketToken === (view?.ticket?.ticketToken || ticketToken);
  const shouldCelebrateTicket = Boolean(view && state.isUsable && (type === "raffle" || hasCouponClaimCelebration));
  const celebrationKey = [
    type,
    view?.ticket?.ticketToken || ticketToken,
    view?.ticket?.ticketStatus || "",
    view?.ticket?.redeemedAtMs || 0,
    view?.raffle?.claimEndAt || "",
  ].join(":");
  const handleFixedAction = () => {
    if (result.actionPath) navigate(result.actionPath);
  };

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!shouldCelebrateTicket || !celebrationTargetRef.current || !celebrationKey) return;
    if (celebratedTicketRef.current === celebrationKey) return;
    celebratedTicketRef.current = celebrationKey;
    party.confetti(celebrationTargetRef.current, {
      count: party.variation.range(70, 110),
      spread: party.variation.range(58, 78),
      speed: party.variation.range(430, 680),
      size: party.variation.range(0.7, 1.15),
    });
  }, [celebrationKey, shouldCelebrateTicket]);

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 2, sm: 3 },
        pt: { xs: 1.5, sm: 2 },
        pb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 132px)`,
        display: "grid",
        placeItems: "start center",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 600,
          mt: { xs: 0.5, sm: 1 },
          mb: 1,
          borderRadius: 1,
          overflow: "hidden",
          scrollMarginTop: `calc(${APP_SHELL_LAYOUT.TOP_APP_BAR_OFFSET_CSS} + 8px)`,
          scrollMarginBottom: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 8px)`,
          border:
            theme.palette.mode === "dark"
              ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
              : `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 18px 58px ${alpha(theme.palette.common.black, 0.34)}`
              : "0 12px 34px rgba(15,23,42,0.055)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 7 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2.25}>
              {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

              {view ? (
                <>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      src={String(view?.raffle?.business?.avatarUrl || "").trim() || undefined}
                      sx={{ bgcolor: alpha(theme.palette.success.main, 0.18), color: theme.palette.success.main, fontWeight: 800 }}
                    >
                      {businessInitial}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {businessName || "Inssa"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isCoupon ? "Digital coupon" : "Digital raffle entry"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    ref={celebrationTargetRef}
                    sx={{
                      p: { xs: 2.25, sm: 2.75 },
                      borderRadius: 1.25,
                      bgcolor: alpha(resultColor, theme.palette.mode === "dark" ? 0.16 : 0.09),
                      border:
                        theme.palette.mode === "dark"
                          ? `1px solid ${alpha(resultColor, 0.18)}`
                          : `1px solid ${alpha(resultColor, 0.16)}`,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <ConfirmationNumberOutlinedIcon sx={{ color: resultColor }} />
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                          {isCoupon ? "Offer pass" : "Ticket pass"}
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                        <Box
                          sx={{
                            width: 54,
                            height: 54,
                            flexShrink: 0,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            color: resultColor,
                            bgcolor: alpha(resultColor, theme.palette.mode === "dark" ? 0.18 : 0.12),
                          }}
                        >
                          <ResultIcon icon={result.icon} sx={{ fontSize: 34 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="overline" sx={{ color: resultColor, fontWeight: 900, lineHeight: 1 }}>
                            {result.eyebrow}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05, letterSpacing: 0 }}>
                            {result.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            {redemptionMessage}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>

                  {heroImageUrl ? (
                    <Box
                      component="img"
                      src={heroImageUrl}
                      alt={view?.raffle?.title || (isCoupon ? "Coupon" : "Raffle")}
                      sx={{ width: "100%", aspectRatio: heroImageAspectRatio, maxHeight: 360, objectFit: "cover", borderRadius: 1 }}
                    />
                  ) : null}

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      {isCoupon ? "Coupon" : "Raffle"}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 850, lineHeight: 1.2 }}>
                      {view?.raffle?.title || (isCoupon ? "Your coupon" : "Your ticket")}
                    </Typography>
                  </Box>

                  {prizeTitle ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {isCoupon ? "Offer" : "Prize"}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {prizeTitle}
                      </Typography>
                      {prizeDescription ? <Typography variant="body2" color="text.secondary">{prizeDescription}</Typography> : null}
                    </Box>
                  ) : null}

                  <OfficialRulesDisclosure
                    text={governingTermsText}
                    title={governingTermsTitle}
                    helperText={governingTermsHelperText}
                  />
                </>
              ) : null}
            </Stack>
          )}
        </CardContent>
      </Card>
      {!loading && view && showQr ? (
        <ClaimRedeemQrAction
          redeemUrl={redeemUrl}
          actionLabel={fixedActionLabel}
          helperText="Tap to enlarge"
        />
      ) : null}
      {!loading && view && !showQr && fixedActionLabel ? (
        <ClaimFixedAction testId="ticket-fixed-action" maxWidth={600}>
          <Button
            fullWidth
            variant={result.actionPath === "/" ? "contained" : "outlined"}
            color={result.actionPath === "/" ? "success" : "inherit"}
            onClick={handleFixedAction}
            sx={(theme) => ({
              py: 1.25,
              fontWeight: 900,
              ...(result.actionPath === "/" ? getSuccessContainedDarkTextSx(theme) : {}),
            })}
          >
            {fixedActionLabel}
          </Button>
        </ClaimFixedAction>
      ) : null}
    </Box>
  );
}
